import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { formatError } from "../lib/api";
import { Brand } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ArrowRight, ShieldCheck, Loader2, ArrowLeft, Lock } from "lucide-react";
import { toast } from "sonner";

export default function SuperAdminLogin() {
  const { login, clearSession } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      if (u.role !== "super_admin") {
        clearSession();
        toast.error("This portal is for platform super administrators only.");
        return;
      }
      toast.success(`Welcome, ${u.name}`);
      navigate("/app/super-admin");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen pm-os-bg flex items-center justify-center p-4">
      <button onClick={() => navigate("/")} data-testid="superadmin-back-home"
        className="fixed top-5 left-5 z-50 flex items-center gap-2 glass rounded-full px-4 h-10 text-sm font-medium text-[#0f2547] hover:bg-white/80 pm-transition">
        <ArrowLeft className="w-4 h-4" /> Back to Main
      </button>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="w-full max-w-md glass-strong rounded-[32px] overflow-hidden shadow-2xl" data-testid="superadmin-login-card">
        <div className="pm-gradient-dark px-9 pt-9 pb-8 text-white relative overflow-hidden">
          <div className="absolute -right-16 -top-16 w-56 h-56 rounded-full bg-[#0e7aa6]/30 blur-3xl" />
          <div className="relative z-10">
            <Brand size="md" dark />
            <div className="mt-8 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 text-xs font-semibold uppercase tracking-wider">
              <Lock className="w-3.5 h-3.5" /> Platform Console
            </div>
            <h1 className="font-display text-3xl font-semibold leading-tight mt-4">Super Admin Portal</h1>
            <p className="text-indigo-100/75 text-sm mt-2 font-body">Restricted access — manage tenants, plans and platform-wide settings.</p>
          </div>
        </div>
        <div className="p-9 bg-white/60">
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-slate-600 font-body">Super admin email</Label>
              <Input data-testid="superadmin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="superadmin@proclytic.io" className="mt-1.5 bg-white border-slate-200 h-12 rounded-2xl" required />
            </div>
            <div>
              <Label className="text-slate-600 font-body">Password</Label>
              <Input data-testid="superadmin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 bg-white border-slate-200 h-12 rounded-2xl" required />
            </div>
            <Button data-testid="superadmin-submit" type="submit" disabled={loading} className="w-full h-12 rounded-full pm-gradient-dark text-white font-semibold pm-transition hover:opacity-90 border-0 shadow-lg shadow-sky-900/25">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ShieldCheck className="w-4 h-4 mr-1" /> Enter console <ArrowRight className="w-4 h-4 ml-1" /></>}
            </Button>
          </form>
          <button onClick={() => navigate("/login")} data-testid="superadmin-to-user-login"
            className="mt-5 w-full text-center text-xs text-slate-400 hover:text-[#0e7aa6] pm-transition">
            Not a super admin? Go to standard sign in
          </button>
        </div>
      </motion.div>
    </div>
  );
}
